/**
 * Bookmarks API Client
 * Gère toutes les communications avec le backend Drupal via un proxy
 */

import type {
  Bookmark,
  BookmarkBulkCheckRequest,
  BookmarkBulkCheckResponse,
  BookmarkCheckRequest,
  BookmarksListResponse,
  BookmarkToggleRequest,
  BookmarkToggleResponse,
} from "../types/bookmark.types"

const API_PROXY_URL = "/api/bookmarks/proxy"
const API_BOOKMARKS_PATH = "/api/bookmarks"

interface ApiOptions {
  token?: string
  userEmail?: string
}

/**
 * Créer les headers pour les requêtes via le proxy
 * L'authentification est gérée par le proxy côté serveur
 */
async function createHeaders(options?: ApiOptions): Promise<HeadersInit> {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  }

  // L'email utilisateur est passé au proxy qui le transmettra à Drupal
  const userEmail = options?.userEmail
  if (userEmail) {
    headers["X-User-Email"] = userEmail
  }

  return headers
}

/**
 * Gestion des erreurs API
 */
class BookmarkApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status?: number,
    public details?: any
  ) {
    super(message)
    this.name = "BookmarkApiError"
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      status: this.status,
      details: this.details,
    }
  }
}

/**
 * Toggle un bookmark (ajouter ou supprimer)
 */
export async function toggleBookmark(
  request: BookmarkToggleRequest,
  options?: ApiOptions
): Promise<BookmarkToggleResponse> {
  const endpoint = `${API_BOOKMARKS_PATH}/toggle`

  console.log("[toggleBookmark] Request:", {
    endpoint,
    request,
    headers: await createHeaders(options),
  })

  try {
    // Appeler le proxy au lieu du backend directement
    const response = await fetch(API_PROXY_URL, {
      method: "POST",
      headers: await createHeaders(options),
      body: JSON.stringify({
        endpoint,
        method: "POST",
        data: request,
      }),
    })

    console.log("[toggleBookmark] Response status:", response.status)

    const result = await response.json()

    if (!response.ok) {
      console.error("[toggleBookmark] Error response:", result)

      throw new BookmarkApiError(
        result.message || result.error || `API error: ${response.status}`,
        result.code || "TOGGLE_FAILED",
        response.status,
        result
      )
    }

    console.log("[toggleBookmark] Success:", result)
    return result
  } catch (error) {
    console.error("[toggleBookmark] Caught error:", error)

    if (error instanceof BookmarkApiError) {
      throw error
    }

    // Erreur réseau ou autre
    throw new BookmarkApiError(
      error instanceof Error
        ? error.message
        : "Network error while toggling bookmark",
      "NETWORK_ERROR",
      undefined,
      {
        originalError: error,
        endpoint,
        request,
      }
    )
  }
}

/**
 * Récupère tous les bookmarks de l'utilisateur
 */
export async function getUserBookmarks(
  page = 1,
  perPage = 50,
  options?: ApiOptions
): Promise<BookmarksListResponse> {
  try {
    const userEmail = options?.userEmail
    if (!userEmail) {
      throw new BookmarkApiError(
        "User email is required",
        "NO_USER_EMAIL",
        401
      )
    }

    const params = new URLSearchParams({
      page: String(page),
      per_page: String(perPage),
    })

    const endpoint = `${API_BOOKMARKS_PATH}/user/${encodeURIComponent(userEmail)}?${params}`

    const response = await fetch(`${API_PROXY_URL}?endpoint=${encodeURIComponent(endpoint)}`, {
      method: "GET",
      headers: await createHeaders(options),
    })

    const result = await response.json()

    if (!response.ok) {
      throw new BookmarkApiError(
        result.message || result.error || "Failed to fetch bookmarks",
        result.code || "FETCH_FAILED",
        response.status,
        result
      )
    }

    return result
  } catch (error) {
    if (error instanceof BookmarkApiError) {
      throw error
    }
    throw new BookmarkApiError(
      "Network error while fetching bookmarks",
      "NETWORK_ERROR",
      undefined,
      error
    )
  }
}

/**
 * Supprime un bookmark spécifique
 */
export async function deleteBookmark(
  bookmarkId: string,
  options?: ApiOptions
): Promise<{ success: boolean }> {
  try {
    const endpoint = `${API_BOOKMARKS_PATH}/${bookmarkId}`

    const response = await fetch(API_PROXY_URL, {
      method: "POST",
      headers: await createHeaders(options),
      body: JSON.stringify({
        endpoint,
        method: "DELETE",
      }),
    })

    const result = await response.json()

    if (!response.ok) {
      throw new BookmarkApiError(
        result.message || result.error || "Failed to delete bookmark",
        result.code || "DELETE_FAILED",
        response.status,
        result
      )
    }

    return result
  } catch (error) {
    if (error instanceof BookmarkApiError) {
      throw error
    }
    throw new BookmarkApiError(
      "Network error while deleting bookmark",
      "NETWORK_ERROR",
      undefined,
      error
    )
  }
}

/**
 * Vérifie si un item est bookmarké
 */
export async function checkBookmark(
  request: BookmarkCheckRequest,
  options?: ApiOptions
): Promise<{ bookmarked: boolean; bookmark?: Bookmark }> {
  try {
    const params = new URLSearchParams({
      entity_type: request.entityType,
      entity_id: request.entityId,
    })

    const endpoint = `${API_BOOKMARKS_PATH}/check?${params}`

    const response = await fetch(`${API_PROXY_URL}?endpoint=${encodeURIComponent(endpoint)}`, {
      method: "GET",
      headers: await createHeaders(options),
    })

    const result = await response.json()

    if (!response.ok) {
      throw new BookmarkApiError(
        result.message || result.error || "Failed to check bookmark",
        result.code || "CHECK_FAILED",
        response.status,
        result
      )
    }

    return result
  } catch (error) {
    if (error instanceof BookmarkApiError) {
      throw error
    }
    throw new BookmarkApiError(
      "Network error while checking bookmark",
      "NETWORK_ERROR",
      undefined,
      error
    )
  }
}

/**
 * Vérifie plusieurs items en une seule requête (optimisation)
 */
export async function bulkCheckBookmarks(
  request: BookmarkBulkCheckRequest,
  options?: ApiOptions
): Promise<BookmarkBulkCheckResponse> {
  try {
    const endpoint = `${API_BOOKMARKS_PATH}/bulk-check`

    const response = await fetch(API_PROXY_URL, {
      method: "POST",
      headers: await createHeaders(options),
      body: JSON.stringify({
        endpoint,
        method: "POST",
        data: request,
      }),
    })

    const result = await response.json()

    if (!response.ok) {
      throw new BookmarkApiError(
        result.message || result.error || "Failed to bulk check bookmarks",
        result.code || "BULK_CHECK_FAILED",
        response.status,
        result
      )
    }

    return result
  } catch (error) {
    if (error instanceof BookmarkApiError) {
      throw error
    }
    throw new BookmarkApiError(
      "Network error while bulk checking bookmarks",
      "NETWORK_ERROR",
      undefined,
      error
    )
  }
}

/**
 * Export de la classe d'erreur pour gestion externe
 */
export { BookmarkApiError }

